// MA-375 — one-shot reconnect retry for a guarded math/reading levels push.
//
// updateStudentLevelsSafe (supabaseClient.js) is a server-side CAS keyed on
// levels_updated_at (MA-39): safe to retry with the identical payload any
// number of times from any caller. This helper is the ONE reconnect
// mechanism for that write (reused at both call sites — App.jsx's pull-side
// merge effect and ParentSettingsSections.tsx's explicit "apply level"
// button) rather than a second bespoke queue, per the MA-375 build note.
//
// On failure it registers a single registerOnlineCallback retry of the SAME
// payload and unregisters itself once that retry fires (success or failure)
// — it only covers "still in this tab, reconnects soon"; a later app boot
// re-deriving "local still newer" (App/levelsPullMerge.js) is the durable
// fallback for a tab that closes before reconnecting. Rejects with the
// original error so callers keep their own error UI/telemetry.

import { updateStudentLevelsSafe } from "@/supabaseClient.js";
import { registerOnlineCallback } from "@/lib/reachability";

export interface LevelsPushPayload {
  mathLevelId: string;
  readingLevelId: string;
  levelsUpdatedAt: string;
}

export interface LevelsPushDeps {
  updateLevels: typeof updateStudentLevelsSafe;
  onReconnect: typeof registerOnlineCallback;
}

const defaultDeps: LevelsPushDeps = {
  updateLevels: updateStudentLevelsSafe,
  onReconnect: registerOnlineCallback,
};

export function pushLevelsWithRetry(
  studentId: string,
  payload: LevelsPushPayload,
  deps: LevelsPushDeps = defaultDeps,
): Promise<unknown> {
  return deps.updateLevels(studentId, payload).catch((err: unknown) => {
    const unregister = deps.onReconnect(() => {
      unregister();
      void deps.updateLevels(studentId, payload).catch(() => {});
    });
    throw err;
  });
}
